import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Line, LineChart, CartesianGrid, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer } from "recharts";
import { tournamentLineScoresQueryOptions } from "@/api/tournamentLineScores";
import { type Match } from "@/api/tournaments";
import { useIsMobile } from "@/hooks/use-mobile";

const LINE_COLORS = {
    line1: "var(--color-chart-blue)",
    line2: "var(--color-win)",
    line3: "var(--color-draw)",
    line4: "var(--color-general-red)",
};

type LineKey = keyof typeof LINE_COLORS;

interface LineDataPoint {
    date: string;
    matchIndex: number;
    matchId: number;
    result: string;
    chosenTeamScore: number;
    opponentScore: number;
    enemyTeam: string;
    line1?: number;
    line2?: number;
    line3?: number;
    line4?: number;
}

interface TournamentLinePerformanceChartProps {
    tournamentId: number;
    teamId: number;
    tournamentMatches?: Match[];
}

export const TournamentLinePerformanceChart = ({
    tournamentId,
    teamId,
    tournamentMatches,
}: TournamentLinePerformanceChartProps) => {
    const { data: lineScoresData, isLoading } = useQuery(tournamentLineScoresQueryOptions(tournamentId, teamId));
    const isMobile = useIsMobile();

    const chartData = useMemo(() => {
        if (!lineScoresData?.matchesAVG || lineScoresData.matchesAVG.length === 0) return [];

        const sorted = [...lineScoresData.matchesAVG].sort((a, b) => a.matchId - b.matchId);

        return sorted.map((match, index) => {
            const tournamentMatch = tournamentMatches?.find((m) => m.id === match.matchId);
            const date = tournamentMatch?.date
                ? new Date(tournamentMatch.date).toLocaleDateString("en-GB")
                : `#${match.matchId}`;

            const dataPoint: LineDataPoint = {
                date,
                matchIndex: index + 1,
                matchId: match.matchId,
                result: match.result,
                chosenTeamScore: match.chosenTeamScore,
                opponentScore: match.opponentScore,
                enemyTeam: match.enemyTeam,
            };

            match.lineAVG.forEach((lineAvg) => {
                if (lineAvg.lineName === "Line 1") {
                    dataPoint.line1 = lineAvg.totalAVG;
                } else if (lineAvg.lineName === "Line 2") {
                    dataPoint.line2 = lineAvg.totalAVG;
                } else if (lineAvg.lineName === "Line 3") {
                    dataPoint.line3 = lineAvg.totalAVG;
                } else if (lineAvg.lineName === "Line 4") {
                    dataPoint.line4 = lineAvg.totalAVG;
                }
            });

            return dataPoint;
        });
    }, [lineScoresData, tournamentMatches]);

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-on-surface-variant">Loading...</div>
            </div>
        );
    }

    if (!chartData || chartData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-on-surface-variant">No data available</div>
            </div>
        );
    }

    const allValues = chartData.flatMap((d) =>
        [d.line1, d.line2, d.line3, d.line4].filter((v): v is number => v !== undefined)
    );
    const minVal = Math.floor(Math.min(...allValues) / 10) * 10;
    const maxVal = Math.ceil(Math.max(...allValues) / 10) * 10;
    const step = Math.max(10, Math.round((maxVal - minVal) / 5 / 10) * 10);
    const referenceLines = [];
    for (let v = minVal; v <= maxVal; v += step) referenceLines.push(v);

    return (
        <div className="flex-1 min-h-0 w-full h-full flex flex-col overflow-x-auto overflow-y-hidden">
            <div
                style={{
                    minWidth: isMobile ? `${Math.max(300, chartData.length * 44)}px` : "100%",
                    height: "100%",
                }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" vertical={false} />
                        {referenceLines.map((v) => (
                            <ReferenceLine
                                key={v}
                                y={v}
                                stroke="rgba(148, 163, 184, 0.35)"
                                strokeDasharray="4 3"
                                label={{
                                    value: v,
                                    position: "insideLeft",
                                    fill: "var(--color-gray-400)",
                                    fontSize: 10,
                                    dx: -28,
                                }}
                            />
                        ))}
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={
                                isMobile || chartData.length > 20
                                    ? (props: { x?: number; y?: number; index?: number }) => (
                                          <text
                                              x={props.x}
                                              y={(props.y ?? 0) + 12}
                                              fill="var(--color-gray-400)"
                                              fontSize={isMobile ? 8 : 10}
                                              textAnchor="middle"
                                          >
                                              {(props.index ?? 0) + 1}
                                          </text>
                                      )
                                    : { fill: "var(--color-gray-400)", fontSize: 10 }
                            }
                            type="category"
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                        />
                        <YAxis domain={[minVal, maxVal]} hide />
                        <Tooltip
                            cursor={{
                                stroke: "rgba(148, 163, 184, 0.4)",
                                strokeWidth: 2,
                                strokeDasharray: "5 5",
                            }}
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;

                                const matchData = payload[0]?.payload as LineDataPoint | undefined;
                                if (!matchData) return null;

                                const sortedPayload = [...payload].sort((a, b) => {
                                    const aVal = typeof a.value === "number" ? a.value : 0;
                                    const bVal = typeof b.value === "number" ? b.value : 0;
                                    return bVal - aVal;
                                });

                                return (
                                    <div className="chart-tooltip">
                                        <div className="flex flex-col gap-2">
                                            <div className="font-semibold text-sm text-general-blue">
                                                Match {matchData.matchIndex} - {matchData.date}
                                            </div>
                                            <div className="text-xs text-on-surface flex items-center gap-2">
                                                <span>vs {matchData.enemyTeam}</span>
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-bold ${
                                                        matchData.result === "W"
                                                            ? "bg-win-background text-win"
                                                            : matchData.result === "D"
                                                              ? "bg-draw-background text-draw"
                                                              : "bg-loss-background text-loss"
                                                    }`}
                                                >
                                                    {matchData.result} {matchData.chosenTeamScore}-
                                                    {matchData.opponentScore}
                                                </span>
                                            </div>
                                            <div className="space-y-2 pt-1 border-t border-outline-variant/20">
                                                {sortedPayload.map((entry) => {
                                                    const color = LINE_COLORS[entry.dataKey as LineKey];
                                                    return (
                                                        <div
                                                            key={entry.dataKey}
                                                            className="flex items-center justify-between gap-3"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{ backgroundColor: color }}
                                                                />
                                                                <span className="text-on-surface text-xs">
                                                                    {entry.name}:
                                                                </span>
                                                            </div>
                                                            <span className="font-bold text-sm" style={{ color }}>
                                                                {entry.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        {([1, 2, 3, 4] as const).map((lineNum) => {
                            const key = `line${lineNum}` as LineKey;
                            const color = LINE_COLORS[key];
                            return (
                                <Line
                                    key={key}
                                    dataKey={key}
                                    stroke={color}
                                    strokeWidth={3}
                                    dot={{ fill: color, r: 4 }}
                                    activeDot={{ r: 6, fill: color, strokeWidth: 2, stroke: "white" }}
                                    name={`Line ${lineNum}`}
                                    isAnimationActive={true}
                                    connectNulls={true}
                                />
                            );
                        })}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
